import * as React from 'react'
import * as z from 'zod'

import {
  type CustomCell,
  type CustomRenderer,
  GridCellKind,
  type ProvideEditorCallback,
  TextCellEntry,
  getMiddleCenterBias,
} from '@glideapps/glide-data-grid'
import { Button } from '~/sotano-kit/primitives/button'
import { parseColorValue } from '~/lib/color'

export const colorPickerCellSchema = z.object({
  kind: z.literal('color-picker-cell'),
  value: z.string(),
  displayValue: z.string(),
})

export type ColorPickerCellProps = z.infer<typeof colorPickerCellSchema>

export type ColorPickerCell = CustomCell<ColorPickerCellProps>

const Editor: ReturnType<ProvideEditorCallback<ColorPickerCell>> = (cell) => {
  const cellData = cell.value.data
  const { value, displayValue } = cellData
  const colorInputRef = React.useRef<HTMLInputElement>(null)

  if (cell.value.readonly) {
    return (
      <TextCellEntry
        highlight={true}
        autoFocus={false}
        disabled={true}
        value={displayValue ?? ''}
        onChange={() => undefined}
      />
    )
  }

  return (
    <div className="relative w-full flex items-center gap-2 cursor-default">
      <Button
        onClick={() => colorInputRef.current?.click()}
        unstyled
        style={{ backgroundColor: value ?? '#000000' }}
        className="w-[30px] h-[30px] rounded-md border border-dark-border-solid cursor-default"
        focusStyled
      />
      <input
        type="text"
        autoFocus
        className="min-h-[26px] border-none outline-none bg-transparent text-dark-muted text-xs"
        defaultValue={value}
        minLength={4}
        maxLength={9}
        onChange={(event) => {
          let value = event.target.value

          // Ensure the first character is always '#'
          if (value[0] !== '#') value = '#' + value.slice(1)

          // Remove any characters that are not hexadecimal (0-9, A-F)
          value = '#' + value.slice(1).replace(/[^0-9A-Fa-f]/g, '')

          // Limit the input to 7 characters (# + 6 hex digits)
          value = value.slice(0, 9)

          if (!parseColorValue(value)) return
          event.target.value = value
          cell.onChange({
            ...cell.value,
            data: {
              ...cell.value.data,
              value,
            },
          })
        }}
      />

      <input
        ref={colorInputRef}
        className="min-h-[26px] border-none outline-none bg-transparent invisible absolute top-0 left-0"
        required
        type="color"
        defaultValue={value ?? ''}
        autoFocus
        onChange={(event) => {
          cell.onChange({
            ...cell.value,
            data: {
              ...cell.value.data,
              value: event.target.value,
            },
          })
        }}
      />
    </div>
  )
}

export const ColorPickerRenderer: CustomRenderer<ColorPickerCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is ColorPickerCell =>
    (cell.data as any).kind === 'color-picker-cell',
  needsHover: false,
  needsHoverPosition: false,
  draw: (args, cell) => {
    const { ctx, rect, theme } = args
    const { displayValue, value } = cell.data

    const xPad = theme.cellHorizontalPadding
    const yPad = theme.cellHorizontalPadding

    const drawX = rect.x + xPad

    ctx.save()
    ctx.beginPath()
    ctx.roundRect(drawX, rect.y + yPad / 2, 26, 26, 4)
    ctx.lineWidth = 1.2
    ctx.strokeStyle = theme.borderColor
    ctx.stroke()
    ctx.fillStyle = displayValue ?? value ?? '#000'
    ctx.fill()
    ctx.globalAlpha = 1

    ctx.font = theme.baseFontFull
    ctx.fillStyle = theme.textDark
    ctx.fillText(
      displayValue,
      drawX + 26 + xPad,
      rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
    )

    ctx.restore()

    return true
  },
  measure: (ctx, cell, theme) => {
    const { displayValue } = cell.data
    return ctx.measureText(displayValue).width + theme.cellHorizontalPadding * 2
  },
  provideEditor: () => ({ editor: Editor }),
  onPaste: (v, d) => {
    const parsedColor = parseColorValue(v)
    if (!parsedColor) return d

    return { ...d, value: v }
  },
}
